import { ArrowDown, ArrowUp } from 'lucide-react';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';

// May 15 batch — top-of-page sort affordance shared across the
// Property Portal Devices + Units tabs, the /properties index, and
// /admin/salt-unit-types. Wraps a Select for "sort by" + a small
// asc/desc toggle button. The component is intentionally stateless —
// callers own sortBy / sortDir and decide what the values mean.
//
// Sortable column headers (where they exist) still work and reflect
// the same state — this toolbar is the canonical UX especially on
// mobile, where the column-header click target is awkward to reach.

interface SortToolbarProps<K extends string> {
  label?: string;
  sortBy: K;
  sortDir: 'asc' | 'desc';
  onSortByChange: (next: K) => void;
  onSortDirChange: (next: 'asc' | 'desc') => void;
  options: { value: K; label: string }[];
  className?: string;
}

export function SortToolbar<K extends string>({
  label = 'Sort',
  sortBy,
  sortDir,
  onSortByChange,
  onSortDirChange,
  options,
  className,
}: SortToolbarProps<K>) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn('inline-flex items-end gap-2', className)}
    >
      <div className="w-48">
        <Select
          label={label}
          value={sortBy}
          onChange={(v) => onSortByChange(v as K)}
          options={options}
        />
      </div>
      <button
        type="button"
        onClick={() => onSortDirChange(sortDir === 'asc' ? 'desc' : 'asc')}
        title={sortDir === 'asc' ? 'Ascending (click to flip)' : 'Descending (click to flip)'}
        className={cn(
          'inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600',
          'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400',
          'hover:text-gray-900 dark:hover:text-gray-100 hover:border-gray-400 dark:hover:border-gray-500',
          'transition-colors',
        )}
        aria-label={sortDir === 'asc' ? 'Sort ascending' : 'Sort descending'}
      >
        {sortDir === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
      </button>
    </div>
  );
}
